import FormGroupInput, { FormGroupInputType } from "../formGroups/FormGroupInput";
import { useCallback, useContext, useEffect, useState } from "react";
import { ContextAlerta } from "../../contexts/ContextAlert";
import { NewOrganization } from "../../types/organization";
import { Formik, FormikHelpers } from "formik";
import { Form, Row } from "react-bootstrap";

import FormGroupGeneric from "../formGroups/FormGroupGeneric";
import utils from "../../utils";

import * as yup from "yup";

//TYPES
export interface IFormOrganizationProps {
    formId?: string;
    onSubmit: (organization: NewOrganization) => void | Promise<void>;
    valoresIniciais: NewOrganization;
}

const validFileExtensions = { image: ["jpg", "png", "jpeg"] };
const MAX_FILE_SIZE = 1024 * 1024 * 5; // 5MB

interface ILogoPreviewProps {
    logo: File | string | null | undefined;
}

function LogoPreview({ logo }: ILogoPreviewProps) {
    const [previewUrl, setPreviewUrl] = useState<string | undefined>();

    useEffect(() => {
        if (!logo) {
            setPreviewUrl(undefined);
            return;
        }

        if (typeof logo === "string") {
            setPreviewUrl(logo);
            return;
        }

        const objectUrl = URL.createObjectURL(logo);
        setPreviewUrl(objectUrl);

        return () => URL.revokeObjectURL(objectUrl);
    }, [logo]);

    return (
        <div className={`d-flex ratio ratio-1x1 w-25 ${!previewUrl && "rounded bg-secondary opacity-50"}`}>
            <img
                src={previewUrl}
                alt="logo"
                className={`h-100 w-100 ${!previewUrl && "opacity-0"} bg-opacity-25 object-fit-cover`}
            />
        </div>
    );
}

//FORM
export default function FormOrganization(props: IFormOrganizationProps) {
    //CONTEXTOS
    const { setPerigo } = useContext(ContextAlerta);

    //VARIAVEIS
    const { onSubmit, valoresIniciais, formId } = props;

    const onSubmitFormik = useCallback(
        async (organization: NewOrganization, helpers: FormikHelpers<NewOrganization>) => {
            try {
                await onSubmit(organization);
                helpers.setSubmitting(false);
                helpers.resetForm();
            } catch (err) {
                if (utils.blErroCancelamento(err)) return;
                setPerigo(utils.retornaMensagemErro(err));
                console.error(err);
            }
        },
        [onSubmit, setPerigo]
    );

    return (
        <Formik
            data-test="form-organization"
            validateOnChange={false}
            validationSchema={yupOrganizationSchema}
            onSubmit={onSubmitFormik}
            initialValues={valoresIniciais}
        >
            {({ handleSubmit, values, errors, setValues }) => {
                return (
                    <Form id={formId} onSubmit={handleSubmit}>
                        <Row className="row-gap-2">
                            <div className="d-flex w-100">
                                <LogoPreview logo={values.logo} />
                                <div className="d-flex w-75 justify-content-start">
                                    <FormGroupGeneric
                                        align="start"
                                        label={
                                            <>
                                                <div className="d-flex ms-2 bg-primary rounded text-white px-2">
                                                    Selecionar imagem
                                                </div>
                                            </>
                                        }
                                        type="file"
                                        accept={validFileExtensions.image.map((type) => `.${type}`).join(", ")}
                                        setValue={(e) =>
                                            setValues({ ...values, logo: e.target.files?.item(0) ?? null })
                                        }
                                        error={errors.logo}
                                    />
                                </div>
                            </div>
                            <small className="text-muted ms-1">
                                • Formatos aceitos: <strong>.png; .jpeg; .jpg</strong> <br />• Tamanho máximo do
                                arquivo: <strong>5MB</strong>
                            </small>
                            <small className="text-muted ms-2"></small>

                            <FormGroupInput
                                label="Nome da empresa"
                                type={FormGroupInputType.TEXT}
                                value={values.name}
                                required
                                setValue={(name) => setValues({ ...values, name })}
                                error={errors.name}
                            />
                            <FormGroupInput
                                label="CNPJ da empresa"
                                type={FormGroupInputType.TEXT}
                                value={values.cnpj}
                                mask="00.000.000/0000-00"
                                required
                                setValue={(cnpj) => setValues({ ...values, cnpj })}
                                error={errors.cnpj}
                            />
                        </Row>
                    </Form>
                );
            }}
        </Formik>
    );
}

function isValidFileType(fileName: string, fileType: "image"): boolean {
    return !!fileName && validFileExtensions[fileType].indexOf(fileName.split(".").pop() ?? "") !== -1;
}

export const yupOrganizationSchema: yup.ObjectSchema<NewOrganization> = yup.object({
    logo: yup
        .mixed<File>()
        .test("is-valid-type", "Insira uma imágem válida.", (value) => {
            if (!value || typeof value === "string") return true;
            return isValidFileType(value.name.toLowerCase(), "image");
        })
        .test("is-valid-size", "O tamanho máximo é de 5MB.", (value: any) => {
            if (!value || typeof value === "string") return true;
            return value.size <= MAX_FILE_SIZE;
        })
        .required("Campo necessário.")
        .nullable(),
    cnpj: yup
        .string()
        .required("Campo necessário.")
        .min(14, "Um CNPJ válido deve ter 14 dígitos.")
        .max(14, "Um CNPJ válido deve ter 14 dígitos."),
    name: yup.string().required("Campo necessário.").max(256, "Deve ter no máximo 256 caracteres."),
});
